/**
 * Hamming codes of any size, with the optional overall parity bit that makes them SECDED
 * (single-error-correcting, double-error-detecting).
 *
 * Positions are numbered 1…n. Bits at power-of-two positions (1, 2, 4, 8, …) are parity
 * bits; the others carry data, filled in order from position 3. The parity bit at 2^i
 * checks every position whose binary index has bit i set, which is why the syndrome —
 * the pattern of failed checks, read as a binary number — is the position of a single
 * flipped bit. The extended code adds a bit at position 0 holding the parity of the
 * whole word.
 */

const SEPARATORS = /[\s_]/g;
const BIT_STRING = /^[01]+$/;
const POSITION_LIST = /^[\d\s,;]*$/;
const POSITION_SPLIT = /[\s,;]+/;

/** Longest data word accepted: 247 bits, which needs 8 parity bits (a 255-bit codeword). */
export const MAX_DATA_BITS = 247;

export type Parity = "even" | "odd";

export interface Options {
  /** Add an overall parity bit at position 0 (SECDED). */
  extended: boolean;
  parity: Parity;
}

export interface Layout {
  /** Data bits. */
  k: number;
  /** Hamming parity bits (not counting the optional overall bit). */
  r: number;
  /** Codeword length, k + r. */
  n: number;
  parityPositions: number[];
  /** Positions that hold data, in the order the data bits are placed. */
  dataPositions: number[];
}

function isPowerOfTwo(n: number): boolean {
  return n > 0 && Number.isInteger(Math.log2(n));
}

function buildLayout(n: number, r: number): Layout {
  const parityPositions: number[] = [];
  const dataPositions: number[] = [];
  for (let p = 1; p <= n; p++) {
    (isPowerOfTwo(p) ? parityPositions : dataPositions).push(p);
  }
  return { k: n - r, r, n, parityPositions, dataPositions };
}

/** The layout for k data bits: the fewest r with 2^r ≥ k + r + 1. */
export function layoutForData(k: number): Layout {
  let r = 2;
  while (2 ** r < k + r + 1) {
    r += 1;
  }
  return buildLayout(k + r, r);
}

/**
 * The layout of a codeword of length n, or null when n is not the length of a Hamming
 * word: too short, or a power of two (where the last parity bit would check only itself).
 */
export function layoutForLength(n: number): Layout | null {
  if (n < 3 || isPowerOfTwo(n)) {
    return null;
  }
  const r = Math.floor(Math.log2(n)) + 1;
  return buildLayout(n, r);
}

/** Positions checked by parity bit i: every position whose index has bit i set. */
export function coverage(i: number, n: number): number[] {
  const bit = 2 ** i;
  const out: number[] = [];
  for (let p = 1; p <= n; p++) {
    if (Math.floor(p / bit) % 2 === 1) {
      out.push(p);
    }
  }
  return out;
}

export type BitsError = "empty" | "invalid" | "tooLong";

export type BitsParse =
  | { ok: true; bits: string }
  | { ok: false; error: BitsError };

/** Read a string of 0s and 1s; spaces and underscores are ignored. */
export function parseBits(text: string, maxLength: number): BitsParse {
  const cleaned = text.replace(SEPARATORS, "");
  if (cleaned.length === 0) {
    return { ok: false, error: "empty" };
  }
  if (!BIT_STRING.test(cleaned)) {
    return { ok: false, error: "invalid" };
  }
  if (cleaned.length > maxLength) {
    return { ok: false, error: "tooLong" };
  }
  return { ok: true, bits: cleaned };
}

export interface ParityRow {
  /** Index i of the check: it sits at position 2^i. */
  index: number;
  position: number;
  /** All positions it covers, including itself. */
  covers: number[];
  /** The data bits at the covered positions, in position order. */
  dataBits: string;
  /** Number of 1s among the data bits it covers. */
  ones: number;
  value: 0 | 1;
}

export interface EncodeOutcome {
  layout: Layout;
  options: Options;
  data: string;
  /** Bits by position: index 0 is the overall parity bit (0 when not extended). */
  bits: number[];
  rows: ParityRow[];
  overall: 0 | 1 | null;
  /** The codeword as text: position 0 (if extended) then positions 1…n. */
  codeword: string;
}

function codewordText(bits: number[], n: number, extended: boolean): string {
  return bits.slice(extended ? 0 : 1, n + 1).join("");
}

/** Encode data bits (leftmost bit goes to position 3, the next to 5, 6, 7, 9, …). */
export function encodeHamming(data: string, options: Options): EncodeOutcome {
  const layout = layoutForData(data.length);
  const bits: number[] = new Array(layout.n + 1).fill(0);
  layout.dataPositions.forEach((position, index) => {
    bits[position] = Number(data[index]);
  });
  const target = options.parity === "odd" ? 1 : 0;
  const rows: ParityRow[] = layout.parityPositions.map((position, index) => {
    const covers = coverage(index, layout.n);
    const dataCovers = covers.filter((p) => p !== position);
    const ones = dataCovers.reduce((sum, p) => sum + (bits[p] ?? 0), 0);
    const value = ((ones + target) % 2) as 0 | 1;
    bits[position] = value;
    return {
      index,
      position,
      covers,
      dataBits: dataCovers.map((p) => bits[p]).join(""),
      ones,
      value,
    };
  });
  let overall: 0 | 1 | null = null;
  if (options.extended) {
    const total = bits.slice(1).reduce((sum, bit) => sum + bit, 0);
    overall = ((total + target) % 2) as 0 | 1;
    bits[0] = overall;
  }
  return {
    layout,
    options,
    data,
    bits,
    rows,
    overall,
    codeword: codewordText(bits, layout.n, options.extended),
  };
}

/** Flip the bits at the given positions (0 is the overall bit of an extended word). */
export function flipPositions(
  codeword: string,
  positions: number[],
  extended: boolean
): string {
  const chars = codeword.split("");
  for (const position of positions) {
    const index = extended ? position : position - 1;
    if (index >= 0 && index < chars.length) {
      chars[index] = chars[index] === "1" ? "0" : "1";
    }
  }
  return chars.join("");
}

export type PositionsParse =
  | { ok: true; positions: number[] }
  | { ok: false; error: "invalid" | "outOfRange" };

/** Read a list of bit positions such as "5" or "2, 6". Empty text means none. */
export function parsePositions(
  text: string,
  min: number,
  max: number
): PositionsParse {
  if (!POSITION_LIST.test(text)) {
    return { ok: false, error: "invalid" };
  }
  const positions = text
    .split(POSITION_SPLIT)
    .filter((part) => part !== "")
    .map(Number);
  const unique = [...new Set(positions)];
  if (unique.some((p) => p < min || p > max)) {
    return { ok: false, error: "outOfRange" };
  }
  return { ok: true, positions: unique };
}

export type Verdict =
  | "none"
  | "corrected"
  | "overallBit"
  | "doubleError"
  | "invalidSyndrome";

export interface CheckRowResult {
  index: number;
  position: number;
  covers: number[];
  /** The received bits at the covered positions. */
  bits: string;
  ones: number;
  /** Whether the check passed. */
  ok: boolean;
  /** 0 for a pass, 1 for a failure: this check's bit of the syndrome. */
  syndromeBit: 0 | 1;
}

export interface CheckOutcome {
  layout: Layout;
  options: Options;
  received: string;
  rows: CheckRowResult[];
  /** The failed-check pattern read as a number: 0 means every check passed. */
  syndrome: number;
  /** Whether the overall parity held; null when the word is not extended. */
  overallOk: boolean | null;
  verdict: Verdict;
  /** The position that was flipped back, when the verdict is a correction. */
  errorPosition: number | null;
  /** The codeword after any correction, in the same layout as `received`. */
  corrected: string;
  /** The data bits read from the corrected word. */
  data: string;
}

function resolveVerdict(
  syndrome: number,
  overallOk: boolean | null,
  n: number
): { verdict: Verdict; errorPosition: number | null } {
  if (overallOk === null) {
    if (syndrome === 0) {
      return { verdict: "none", errorPosition: null };
    }
    return syndrome <= n
      ? { verdict: "corrected", errorPosition: syndrome }
      : { verdict: "invalidSyndrome", errorPosition: null };
  }
  if (syndrome === 0) {
    return overallOk
      ? { verdict: "none", errorPosition: null }
      : { verdict: "overallBit", errorPosition: 0 };
  }
  if (overallOk) {
    return { verdict: "doubleError", errorPosition: null };
  }
  return syndrome <= n
    ? { verdict: "corrected", errorPosition: syndrome }
    : { verdict: "invalidSyndrome", errorPosition: null };
}

/**
 * Check a received word: compute each parity check, read the syndrome, and correct a
 * single-bit error. With the overall bit, two errors are told apart from one.
 */
export function checkHamming(
  received: string,
  options: Options
): CheckOutcome | null {
  const n = received.length - (options.extended ? 1 : 0);
  const layout = layoutForLength(n);
  if (!layout) {
    return null;
  }
  const offset = options.extended ? 0 : 1;
  const bits: number[] = new Array(n + 1).fill(0);
  for (let p = options.extended ? 0 : 1; p <= n; p++) {
    bits[p] = Number(received[p - offset]);
  }
  const target = options.parity === "odd" ? 1 : 0;
  const rows: CheckRowResult[] = layout.parityPositions.map(
    (position, index) => {
      const covers = coverage(index, n);
      const ones = covers.reduce((sum, p) => sum + (bits[p] ?? 0), 0);
      const ok = ones % 2 === target;
      return {
        index,
        position,
        covers,
        bits: covers.map((p) => bits[p]).join(""),
        ones,
        ok,
        syndromeBit: ok ? 0 : 1,
      };
    }
  );
  const syndrome = rows.reduce(
    (sum, row) => sum + row.syndromeBit * 2 ** row.index,
    0
  );
  let overallOk: boolean | null = null;
  if (options.extended) {
    const total = bits.reduce((sum, bit) => sum + bit, 0);
    overallOk = total % 2 === target;
  }
  const { verdict, errorPosition } = resolveVerdict(syndrome, overallOk, n);
  const corrected =
    errorPosition === null
      ? received
      : flipPositions(received, [errorPosition], options.extended);
  const data = layout.dataPositions.map((p) => corrected[p - offset]).join("");
  return {
    layout,
    options,
    received,
    rows,
    syndrome,
    overallOk,
    verdict,
    errorPosition,
    corrected,
    data,
  };
}

export interface SyndromeRow {
  /** The syndrome value. */
  syndrome: number;
  /** The position it points at, or null for no error. */
  position: number | null;
  /** Whether the position is a parity bit. */
  parity: boolean;
  /** The position exists in this codeword. */
  valid: boolean;
}

/** What each syndrome value means for a code of length n, up to `limit` rows. */
export function syndromeTable(layout: Layout, limit: number): SyndromeRow[] {
  const total = Math.min(2 ** layout.r, limit);
  const rows: SyndromeRow[] = [];
  for (let syndrome = 0; syndrome < total; syndrome++) {
    rows.push({
      syndrome,
      position: syndrome === 0 ? null : syndrome,
      parity: isPowerOfTwo(syndrome),
      valid: syndrome <= layout.n,
    });
  }
  return rows;
}

/** The role label of a position: P1, P2, P4… for parity, D1, D2… for data. */
export function roleLabels(layout: Layout, extended: boolean): string[] {
  const labels: string[] = extended ? ["P0"] : [];
  let dataIndex = 0;
  for (let p = 1; p <= layout.n; p++) {
    if (isPowerOfTwo(p)) {
      labels.push(`P${p}`);
    } else {
      dataIndex += 1;
      labels.push(`D${dataIndex}`);
    }
  }
  return labels;
}
